import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Filter, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { ApiService } from "../../../services/api";

interface WeekOption {
  id: number;
  name: string;
  value: string;
  startDate: string;
  endDate: string;
}

interface WeekMeta {
  weekNumber: number;
  name: string;
  dateRange: string;
}

interface WeekStats {
  total: number;
  accepted: number;
  pending: number;
  rejected: number;
  cancelled: number;
  weekNumber: number;
}

interface ReportTeacher {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  media?: {
    id: string;
    url: string;
  } | null;
}

interface ReportRow {
  teacher: ReportTeacher;
  total_taken: number;
  accepted: number;
  pending: number;
  rejected: number;
  cancelled: number;
  attended?: number;
  no_show?: number;
  unmarked?: number;
  success_rate?: number;
  attendance_rate?: number;
  weekData: {
    [key: string]: WeekStats;
  };
}

interface ReportResponse {
  data: ReportRow[];
  weeks: WeekMeta[];
  meta: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    perPage: number;
  };
  summary?: {
    total: number;
    attended: number;
    noShow: number;
    unmarked: number;
    attendanceRate: number;
  };
}

export default function WeeklyAppointmentReportTab() {
  const [availableWeeks, setAvailableWeeks] = useState<WeekOption[]>([]);
  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [reportData, setReportData] = useState<ReportResponse | null>(null);
  const [loadingWeeks, setLoadingWeeks] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [filtered, setFiltered] = useState(false);
  const limit = 20;

  useEffect(() => {
    const loadWeeks = async () => {
      setLoadingWeeks(true);
      setError(null);
      try {
        const response = await ApiService.getAppointmentReportWeeks();
        setAvailableWeeks(response || []);
      } catch (err: any) {
        setError(err?.message || "Haftalar yüklenemedi");
        setAvailableWeeks([]);
      } finally {
        setLoadingWeeks(false);
      }
    };

    loadWeeks();
  }, []);

  const loadReport = useCallback(
    async (currentPage: number = 1) => {
      if (selectedWeeks.length === 0) {
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await ApiService.getWeeklyAppointmentReport({
          weekNumbers: selectedWeeks,
          page: currentPage,
          limit,
        });
        setReportData(response);
        setPage(currentPage);
      } catch (err: any) {
        setError(err?.message || "Rapor yüklenemedi");
        setReportData(null);
      } finally {
        setLoading(false);
      }
    },
    [selectedWeeks, limit]
  );

  const isFilterReady = () => {
    return selectedWeeks.length > 0;
  };

  const buildMissingMessage = () => {
    if (selectedWeeks.length === 0) {
      return "Hafta seçiniz.";
    }
    return "";
  };

  const handleFilter = () => {
    if (isFilterReady()) {
      setFiltered(true);
      loadReport(1);
    }
  };

  const handleClearFilters = () => {
    setSelectedWeeks([]);
    setReportData(null);
    setError(null);
    setFiltered(false);
  };

  const handleExport = async () => {
    if (!reportData || !reportData.weeks || selectedWeeks.length === 0 || exporting) return;

    try {
      setExporting(true);
      const blob = await ApiService.exportWeeklyAppointmentReport(selectedWeeks);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "randevu_raporu.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || "Rapor dışa aktarılırken bir hata oluştu");
    } finally {
      setExporting(false);
    }
  };

  const getSuccessColor = (rate: number) => {
    if (rate >= 0.8) return "text-green-600";
    if (rate >= 0.5) return "text-orange-500";
    if (rate > 0) return "text-red-500";
    return "text-gray-500";
  };

  return (
    <div className="h-full flex flex-col">
      {!filtered && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filtreler</h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="col-span-1 md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  Hafta {selectedWeeks.length > 0 && `(${selectedWeeks.length} Seçili)`}
                </label>
                <div>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <div className="max-h-64 overflow-auto divide-y divide-gray-200">
                      {loadingWeeks ? (
                        <div className="px-3 py-2 text-sm text-gray-500 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Yükleniyor...
                        </div>
                      ) : availableWeeks.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Hafta yok.</div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setSelectedWeeks([])}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                              selectedWeeks.length === 0 ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-600"
                            }`}>
                            Hafta Seçiniz
                          </button>
                          {availableWeeks.map((week) => {
                            const isSelected = selectedWeeks.includes(week.id);
                            return (
                              <button
                                key={week.id}
                                type="button"
                                onClick={() => {
                                  const weekSet = new Set(selectedWeeks);
                                  if (weekSet.has(week.id)) {
                                    weekSet.delete(week.id);
                                  } else {
                                    weekSet.add(week.id);
                                  }
                                  setSelectedWeeks(Array.from(weekSet).sort((a, b) => a - b));
                                }}
                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                  isSelected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                                }`}>
                                <span className="font-medium">{week.name}</span>
                                <span className="text-xs text-gray-500 ml-2">({week.value})</span>
                              </button>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 mt-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleFilter}
                disabled={!isFilterReady()}
                className={`px-6 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isFilterReady() ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}>
                Filtrele
              </button>
              {!isFilterReady() && (
                <span className="text-xs text-gray-600">Randevu raporlarını görüntülemek için lütfen {buildMissingMessage()}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {filtered && (
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={handleClearFilters}
              className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300">
              <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Filtrele</span>
            </button>
            {reportData && (
              <button
                onClick={handleExport}
                disabled={exporting || !reportData || !reportData.data.length}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-medium shadow-sm bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-60 disabled:cursor-not-allowed">
                <Download className="w-4 h-4" />
                <span>{exporting ? "Dışa aktarılıyor..." : "Dışa Aktar"}</span>
              </button>
            )}
          </div>

          {reportData && (
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <span>
                {reportData.meta.totalItems > 0
                  ? `${(page - 1) * limit + 1}-${Math.min(page * limit, reportData.meta.totalItems)} / ${reportData.meta.totalItems}`
                  : "0 / 0"}
              </span>
              <button
                onClick={() => loadReport(page - 1)}
                disabled={page <= 1 || loading}
                className="disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronLeft className="h-4 w-4 text-gray-400" />
              </button>
              <button
                onClick={() => loadReport(page + 1)}
                disabled={page >= reportData.meta.totalPages || loading}
                className="disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          )}
        </div>
      )}

      {filtered && error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-red-900">Hata</p>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        {!filtered ? (
          <div className="flex-1 flex items-center justify-center h-full" />
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Rapor yükleniyor...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Hata Oluştu</h3>
            <p className="text-gray-500 max-w-md mb-4">{error}</p>
            <button
              onClick={() => {
                setError(null);
                handleFilter();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Tekrar Dene
            </button>
          </div>
        ) : reportData && reportData.data.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="block md:hidden divide-y divide-gray-200">
              {reportData.data.map((row) => (
                <div key={row.teacher.id} className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden">
                        {row.teacher.media?.url ? (
                          <img
                            src={row.teacher.media.url}
                            alt={`${row.teacher.firstName} ${row.teacher.lastName}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-white text-sm font-medium">
                            {row.teacher.firstName?.[0]}
                            {row.teacher.lastName?.[0]}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {row.teacher.firstName} {row.teacher.lastName}
                        </p>
                        {row.teacher.email && <p className="text-xs text-gray-500">{row.teacher.email}</p>}
                      </div>
                    </div>
                    {selectedWeeks.length > 1 && (
                      <div className="text-right">
                        <div className={`text-sm font-semibold ${getSuccessColor(row.attendance_rate ?? row.success_rate ?? 0)}`}>
                          {((row.attendance_rate ?? row.success_rate ?? 0) * 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-gray-500">Katılım Oranı</div>
                      </div>
                    )}
                  </div>
                  {selectedWeeks.length > 1 && (
                    <>
                      <div className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                        Seçilen {selectedWeeks.length} Haftanın Toplam Verileri
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-xs text-gray-600">
                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{row.total_taken}</div>
                          <div className="text-gray-500">Toplam</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{row.accepted}</div>
                          <div className="text-gray-500">Onaylanan</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{row.pending}</div>
                          <div className="text-gray-500">Bekleyen</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{row.rejected}</div>
                          <div className="text-gray-500">Reddedilen</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{row.cancelled}</div>
                          <div className="text-gray-500">İptal</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{row.attended ?? 0}</div>
                          <div className="text-gray-500">Katıldı</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{row.no_show ?? 0}</div>
                          <div className="text-gray-500">Katılmadı</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{row.unmarked ?? 0}</div>
                          <div className="text-gray-500">İşaretlenmedi</div>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    {reportData.weeks.map((week) => {
                      const weekKey = `week_${week.weekNumber}`;
                      const weekStats = row.weekData[weekKey];
                      const total = weekStats?.total || 0;
                      const accepted = weekStats?.accepted || 0;
                      const pending = weekStats?.pending || 0;
                      const rejected = weekStats?.rejected || 0;
                      const cancelled = weekStats?.cancelled || 0;
                      const attended = (weekStats as any)?.attended || 0;
                      const noShow = (weekStats as any)?.no_show || 0;
                      const unmarked = (weekStats as any)?.unmarked || 0;
                      return (
                        <div key={week.weekNumber} className="py-2 border-t border-gray-100">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{week.name}</p>
                              <p className="text-xs text-gray-500">{week.dateRange}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="text-center">
                              <p className="text-xs text-gray-500">Toplam</p>
                              <p className="text-sm font-semibold text-gray-700">{total}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500">Onaylanan</p>
                              <p className="text-sm font-semibold text-gray-700">{accepted}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500">Bekleyen</p>
                              <p className="text-sm font-semibold text-gray-700">{pending}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500">Reddedilen</p>
                              <p className="text-sm font-semibold text-gray-700">{rejected}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500">İptal</p>
                              <p className="text-sm font-semibold text-gray-700">{cancelled}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500">Katıldı</p>
                              <p className="text-sm font-semibold text-gray-700">{attended}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500">Katılmadı</p>
                              <p className="text-sm font-semibold text-gray-700">{noShow}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500">İşaretlenmedi</p>
                              <p className="text-sm font-semibold text-gray-700">{unmarked}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Danışman</th>
                    {selectedWeeks.length > 1 && (
                      <th
                        colSpan={9}
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-300">
                        Seçilen {selectedWeeks.length} Haftanın Toplam Verileri
                      </th>
                    )}
                    {reportData.weeks.map((week) => (
                      <th
                        key={week.weekNumber}
                        colSpan={8}
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-300">
                        {week.name}
                        <div className="text-xs font-normal text-gray-400 normal-case mt-1">{week.dateRange}</div>
                      </th>
                    ))}
                  </tr>
                  <tr className="bg-gray-100">
                    <th />
                    {selectedWeeks.length > 1 && (
                      <>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 border-l border-gray-300">Toplam</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Onaylanan</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Bekleyen</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Reddedilen</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">İptal</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Katıldı</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Katılmadı</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">İşaretlenmedi</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Onay Oranı</th>
                      </>
                    )}
                    {reportData.weeks.map((week) => (
                      <React.Fragment key={`header-${week.weekNumber}`}>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 border-l border-gray-300">Toplam</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Onaylanan</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Bekleyen</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Reddedilen</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">İptal</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Katıldı</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Katılmadı</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">İşaretlenmedi</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.data.map((row, idx) => (
                    <tr key={row.teacher.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden">
                            {row.teacher.media?.url ? (
                              <img
                                src={row.teacher.media.url}
                                alt={`${row.teacher.firstName} ${row.teacher.lastName}`}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-white text-xs font-medium">
                                {row.teacher.firstName?.[0]}
                                {row.teacher.lastName?.[0]}
                              </span>
                            )}
                          </div>
                          <div>
                            <div className="font-medium">
                              {row.teacher.firstName} {row.teacher.lastName}
                            </div>
                            {row.teacher.email && <div className="text-xs text-gray-500">{row.teacher.email}</div>}
                          </div>
                        </div>
                      </td>
                      {selectedWeeks.length > 1 && (
                        <>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-900 border-l border-gray-300">
                            {row.total_taken}
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-900">{row.accepted}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-900">{row.pending}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-900">{row.rejected}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-900">{row.cancelled}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-900">{row.attended ?? 0}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-900">{row.no_show ?? 0}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-900">{row.unmarked ?? 0}</td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm text-center">
                            <span className={`font-semibold ${getSuccessColor(row.attendance_rate ?? row.success_rate ?? 0)}`}>
                              {((row.attendance_rate ?? row.success_rate ?? 0) * 100).toFixed(0)}%
                            </span>
                          </td>
                        </>
                      )}
                      {reportData.weeks.map((week) => {
                        const weekKey = `week_${week.weekNumber}`;
                        const weekStats = row.weekData[weekKey];
                        const total = weekStats?.total || 0;
                        const accepted = weekStats?.accepted || 0;
                        const pending = weekStats?.pending || 0;
                        const rejected = weekStats?.rejected || 0;
                        const cancelled = weekStats?.cancelled || 0;
                        const attended = (weekStats as any)?.attended || 0;
                        const noShow = (weekStats as any)?.no_show || 0;
                        const unmarked = (weekStats as any)?.unmarked || 0;
                        return (
                          <React.Fragment key={`${row.teacher.id}-week-${week.weekNumber}`}>
                            <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-700 border-l border-gray-300">
                              {total}
                            </td>
                            <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-700">{accepted}</td>
                            <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-700">{pending}</td>
                            <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-700">{rejected}</td>
                            <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-700">{cancelled}</td>
                            <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-700">{attended}</td>
                            <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-700">{noShow}</td>
                            <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-700">{unmarked}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Veri Bulunamadı</h3>
            <p className="text-gray-500 max-w-md mb-4">Seçilen kriterlere uygun randevu raporu bulunamadı.</p>
            <button onClick={handleFilter} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Yenile
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
